import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Paper, 
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert
} from '@mui/material';
import { WORKFLOW_STAGES, COMPLIANCE_RULES } from '../../config/workflowConfig';
import { DocumentUploader } from '../SmartComply/DocumentManager/DocumentUploader';
import { ComplianceChecker } from '../Compliance/ComplianceChecker';
import { ApprovalQueue } from '../Approval/ApprovalQueue';

export const WorkflowManager: React.FC = () => {
  const [currentStage, setCurrentStage] = useState<string>('INITIAL_REQUEST');
  const [workflowData, setWorkflowData] = useState<any>({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [complianceStatus, setComplianceStatus] = useState<any>({});
  const [approvalItems, setApprovalItems] = useState<any[]>([]);
  const [showDocumentUploader, setShowDocumentUploader] = useState(false);

  const validateCurrentStage = async () => {
    const stage = WORKFLOW_STAGES[currentStage];
    const errors: string[] = [];

    // Document validation
    for (const doc of stage.requiredDocuments) {
      if (doc.required && !workflowData.documents?.[doc.id]) {
        errors.push(`Missing required document: ${doc.name}`);
      }
    }

    // Compliance checks
    for (const check of stage.automatedChecks) {
      const result = await check.validation(workflowData);
      if (!result.valid) {
        errors.push(`Compliance check failed: ${check.name}`);
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const moveToNextStage = async () => {
    const isValid = await validateCurrentStage();
    if (!isValid) return;

    const stage = WORKFLOW_STAGES[currentStage];
    if (stage.nextStages.length > 0) {
      setCurrentStage(stage.nextStages[0]);
    }
  };

  const handleDocumentUpload = (files: File[]) => {
    // Process each uploaded file
    files.forEach(file => {
      // Create document data for each file
      const documentData = {
        id: `doc-${Date.now()}-${file.name}`,
        name: file.name,
        type: file.type,
        uploadDate: new Date().toISOString(),
        size: file.size,
        metadata: {
          contentType: file.type,
          lastModified: file.lastModified
        }
      };
      
      // Update workflow data with the new document
      setWorkflowData((prev: any) => ({
        ...prev,
        documents: {
          ...(prev.documents || {}),
          [documentData.id]: documentData
        }
      }));
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5">
            Fiscal Sponsorship Workflow Manager
          </Typography>
          
          <Stepper activeStep={Object.keys(WORKFLOW_STAGES).indexOf(currentStage)}>
            {Object.values(WORKFLOW_STAGES).map((stage) => (
              <Step key={stage.id}>
                <StepLabel>{stage.name}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {validationErrors.map((error, index) => (
            <Alert key={index} severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ))}

          <Button 
            variant="outlined" 
            onClick={() => setShowDocumentUploader(true)}
            sx={{ my: 2 }}
          >
            Upload Required Documents
          </Button>

          {/* Document Uploader - using the correct props */}
          <DocumentUploader
            fundId="demo-fund-id"
            onUpload={handleDocumentUpload}
          />

          {/* Compliance Checker with correct props */}
          <ComplianceChecker
            fiscalSponsorId="demo-sponsor-id"
            checkType="transaction"
            data={workflowData}
            onComplete={(results) => setComplianceStatus(results)}
          />

          <ApprovalQueue
            items={approvalItems}
            onApprove={async (itemId, notes) => {
              console.log('Approving item:', itemId, notes);
            }}
            onReject={async (itemId, notes) => {
              console.log('Rejecting item:', itemId, notes);
            }}
            onView={(item) => {
              console.log('Viewing item:', item);
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={moveToNextStage}
            disabled={validationErrors.length > 0}
            sx={{ mt: 2 }}
          >
            Proceed to Next Stage
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
}; 